/**
 * What to do with each item of an offline-queue flush result
 * (POST /attendance/punch/batch) — pure, so it is unit-tested.
 *
 *   - ok                       → remove from the queue (dedup-safe);
 *   - punch_time_out_of_range  → the punch is too OLD and only ages further:
 *                                remove it and tell the user (it can never
 *                                succeed — keeping it would wedge the queue);
 *   - punch_time_in_future     → the device clock is AHEAD of the server's
 *                                (F113). The same punch is accepted once the
 *                                server clock catches up, so it stays queued
 *                                and is retried later — dropping it forced a
 *                                regularization for a punch that was valid;
 *   - anything else            → stays queued for the next flush (the
 *                                pre-existing behaviour).
 */

import type { BatchPunchResultItem } from "@/types/attendance";

/** Server code: the punch is too old to accept, ever. */
export const PUNCH_TOO_OLD_CODE = "punch_time_out_of_range";
/** Server code: the punch is ahead of the server clock — retry later. */
export const PUNCH_IN_FUTURE_CODE = "punch_time_in_future";
/** How long to wait before re-flushing future-skewed punches. The server
 *  tolerates 5 minutes of skew, so a couple of minutes usually suffices. */
export const FUTURE_SKEW_RETRY_MS = 2 * 60 * 1000;

export interface FlushPlan {
  /** uuids the server accepted — remove them. */
  accepted: string[];
  /** refused for good (too old) — remove them and tell the user. */
  dropped: BatchPunchResultItem[];
  /** device clock ahead — keep queued, flush again after a delay. */
  retryLater: BatchPunchResultItem[];
}

export function planFlush(results: BatchPunchResultItem[]): FlushPlan {
  const plan: FlushPlan = { accepted: [], dropped: [], retryLater: [] };
  for (const r of results) {
    if (r.ok) {
      plan.accepted.push(r.client_punch_uuid);
    } else if (r.error === PUNCH_TOO_OLD_CODE) {
      plan.dropped.push(r);
    } else if (r.error === PUNCH_IN_FUTURE_CODE) {
      plan.retryLater.push(r);
    }
  }
  return plan;
}
